import React from 'react';
import BaseInput from '../Base/BaseInput';
import BaseSelect from '../Base/BaseSelect';
import { User } from '../../models/UserModel';
import { USER_ROLE_SELECT_OPTIONS } from '../../utils/userRoleDisplay';
import PasswordRequirementsList from './PasswordRequirementsList';
import { PASSWORD_MAX_LENGTH } from '../../utils/passwordRules';
import {
  finalizeFormTextField,
  sanitizeFormTextField,
  sanitizeTextInput,
} from '../../utils/textInputSanitize';

interface Props {
  value: User;
  onChange: (u: User) => void;
  onClose?: () => void;
  error?: string | null;
  onSave?: (data: any) => void;
  canResetPassword?: boolean;
}

const ROLE_OPTIONS = USER_ROLE_SELECT_OPTIONS;

const UserForm: React.FC<Props> = ({ value, onChange, error, canResetPassword = false }) => {
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value: v } = e.target;
    if (name === 'is_active') {
      onChange({ ...value, [name]: v === 'true' });
      return;
    }
    onChange({ ...value, [name]: v });
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value: v } = e.target;
    if (name === 'password') {
      onChange({ ...value, [name]: v });
      return;
    }
    onChange({ ...value, [name]: sanitizeFormTextField(name, v) });
  };

  const handleTextBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value: v } = e.target;
    if (name === 'password') return;
    const next = name === 'username' || name === 'email'
      ? sanitizeTextInput(v).trim()
      : finalizeFormTextField(name, v);
    if (next !== (value as any)[name]) {
      onChange({ ...value, [name]: next });
    }
  };

  return (
    <div className="user-form">
      <div className="form-row">
        <div className="form-field">
          <BaseInput
            label="Usuario"
            name="username"
            value={value.username ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            required
          />
        </div>
        <div className="form-field">
          <BaseInput
            label="Correo electrónico"
            type="email"
            name="email"
            value={value.email ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <BaseInput
            label="Nombre"
            name="first_name"
            value={value.first_name ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
          />
        </div>
        <div className="form-field">
          <BaseInput
            label="Apellido"
            name="last_name"
            value={value.last_name ?? ''}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
          />
        </div>
      </div>

      {!value.id && (
        <div className="form-row">
          <div className="form-field">
            <BaseInput
              label="Contraseña"
              type="password"
              name="password"
              value={value.password ?? ''}
              onChange={handleTextChange}
              maxLength={PASSWORD_MAX_LENGTH}
            />
            {(value.password?.length ?? 0) > 0 && (
              <PasswordRequirementsList password={value.password ?? ''} />
            )}
          </div>
        </div>
      )}

      {value.id && canResetPassword && (
        <div className="form-row">
          <div className="form-field">
            <BaseInput
              label="Nueva contraseña"
              type="password"
              name="password"
              value={value.password ?? ''}
              onChange={handleTextChange}
              maxLength={PASSWORD_MAX_LENGTH}
            />
            {(value.password?.length ?? 0) > 0 && (
              <PasswordRequirementsList password={value.password ?? ''} />
            )}
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="form-field">
          <BaseSelect
            label="Rol"
            name="role"
            value={value.role ?? 'GESTOR'}
            onChange={handleSelectChange}
            options={ROLE_OPTIONS}
          />
        </div>
        <div className="form-field">
          <BaseSelect
            label="Estado"
            name="is_active"
            value={String(value.is_active !== false)}
            onChange={handleSelectChange}
            options={[
              { value: 'true', label: 'Activo' },
              { value: 'false', label: 'Inactivo' },
            ]}
          />
        </div>
      </div>

      {error && (
        <div className="form-error" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default UserForm;
